import * as pc from 'playcanvas';
import { createMetalMaterial, createGlowMaterial } from './materials.js';
import { LEVEL_CONFIG } from '../config/levels.js';
import { applyDifficultyToBoss } from '../config/difficulty.js';

// Create a boss entity for the given level
export function createBoss(app, level = 1, difficulty = 'normal') {
    const baseConfig = LEVEL_CONFIG[level];
    const config = applyDifficultyToBoss(baseConfig, difficulty);

    const boss = new pc.Entity('Boss');

    // Main body
    boss.addComponent('render', {
        type: 'box',
        material: createMetalMaterial(config.bossColor)
    });

    // Scale boss slightly larger each level
    const sizeMultiplier = 1 + (level - 1) * 0.15;
    boss.setPosition(0, 2 * sizeMultiplier, -5);
    boss.setLocalScale(2.5 * sizeMultiplier, 3 * sizeMultiplier, 2.5 * sizeMultiplier);
    app.root.addChild(boss);

    // Armor plates
    const armorColor = createMetalMaterial(new pc.Color(
        config.bossColor.r * 0.5,
        config.bossColor.g * 0.5,
        config.bossColor.b * 0.5
    ));

    const chestPlate = new pc.Entity('ChestPlate');
    chestPlate.addComponent('render', { type: 'box', material: armorColor });
    chestPlate.setLocalPosition(0, -0.1, 0.52);
    chestPlate.setLocalScale(0.7, 0.5, 0.05);
    boss.addChild(chestPlate);

    // Glowing eyes - color matches boss theme
    const eyeGlow = createGlowMaterial(config.bossGlowColor, 1.5);
    const eyeInner = createGlowMaterial(new pc.Color(1, 0.9, 0.5), 1.2);

    // Left eye
    const leftEye = new pc.Entity('LeftEye');
    leftEye.addComponent('render', { type: 'box', material: eyeGlow });
    leftEye.setLocalPosition(-0.2, 0.2, 0.52);
    leftEye.setLocalScale(0.2, 0.08, 0.05);
    boss.addChild(leftEye);

    const leftEyeInner = new pc.Entity('LeftEyeInner');
    leftEyeInner.addComponent('render', { type: 'sphere', material: eyeInner });
    leftEyeInner.setLocalPosition(0, 0, 0.3);
    leftEyeInner.setLocalScale(0.3, 0.8, 0.5);
    leftEye.addChild(leftEyeInner);

    // Right eye
    const rightEye = new pc.Entity('RightEye');
    rightEye.addComponent('render', { type: 'box', material: eyeGlow });
    rightEye.setLocalPosition(0.2, 0.2, 0.52);
    rightEye.setLocalScale(0.2, 0.08, 0.05);
    boss.addChild(rightEye);

    const rightEyeInner = new pc.Entity('RightEyeInner');
    rightEyeInner.addComponent('render', { type: 'sphere', material: eyeInner });
    rightEyeInner.setLocalPosition(0, 0, 0.3);
    rightEyeInner.setLocalScale(0.3, 0.8, 0.5);
    rightEye.addChild(rightEyeInner);

    // Horns - more horns for higher levels
    const hornColor = createMetalMaterial(new pc.Color(
        config.bossGlowColor.r * 0.5,
        config.bossGlowColor.g * 0.5,
        config.bossGlowColor.b * 0.5
    ));

    const leftHorn = new pc.Entity('LeftHorn');
    leftHorn.addComponent('render', { type: 'cone', material: hornColor });
    leftHorn.setLocalPosition(-0.35, 0.45, 0.2);
    leftHorn.setLocalScale(0.15, 0.4 + level * 0.1, 0.15);
    leftHorn.setLocalEulerAngles(-30, 0, -20);
    boss.addChild(leftHorn);

    const rightHorn = new pc.Entity('RightHorn');
    rightHorn.addComponent('render', { type: 'cone', material: hornColor });
    rightHorn.setLocalPosition(0.35, 0.45, 0.2);
    rightHorn.setLocalScale(0.15, 0.4 + level * 0.1, 0.15);
    rightHorn.setLocalEulerAngles(-30, 0, 20);
    boss.addChild(rightHorn);

    const centerHorn = new pc.Entity('CenterHorn');
    centerHorn.addComponent('render', { type: 'cone', material: hornColor });
    centerHorn.setLocalPosition(0, 0.5, 0.3);
    centerHorn.setLocalScale(0.12, 0.35 + level * 0.1, 0.12);
    centerHorn.setLocalEulerAngles(-45, 0, 0);
    boss.addChild(centerHorn);

    // Extra horns for higher levels
    if (level >= 2) {
        const extraHorn1 = new pc.Entity('ExtraHorn1');
        extraHorn1.addComponent('render', { type: 'cone', material: hornColor });
        extraHorn1.setLocalPosition(-0.5, 0.3, 0);
        extraHorn1.setLocalScale(0.1, 0.3, 0.1);
        extraHorn1.setLocalEulerAngles(0, 0, -45);
        boss.addChild(extraHorn1);

        const extraHorn2 = new pc.Entity('ExtraHorn2');
        extraHorn2.addComponent('render', { type: 'cone', material: hornColor });
        extraHorn2.setLocalPosition(0.5, 0.3, 0);
        extraHorn2.setLocalScale(0.1, 0.3, 0.1);
        extraHorn2.setLocalEulerAngles(0, 0, 45);
        boss.addChild(extraHorn2);
    }

    if (level >= 3) {
        // Crown of horns
        for (let i = 0; i < 5; i++) {
            const crownHorn = new pc.Entity(`CrownHorn${i}`);
            crownHorn.addComponent('render', {
                type: 'cone',
                material: createGlowMaterial(config.bossGlowColor, 0.5)
            });
            const angle = (i / 5) * Math.PI - Math.PI / 2;
            crownHorn.setLocalPosition(Math.cos(angle) * 0.3, 0.55, Math.sin(angle) * 0.2 + 0.1);
            crownHorn.setLocalScale(0.06, 0.2, 0.06);
            boss.addChild(crownHorn);
        }
    }

    // Shoulder spikes
    const leftShoulder = new pc.Entity('LeftShoulder');
    leftShoulder.addComponent('render', { type: 'cone', material: armorColor });
    leftShoulder.setLocalPosition(-0.55, 0.15, 0);
    leftShoulder.setLocalScale(0.2, 0.35 + level * 0.05, 0.2);
    leftShoulder.setLocalEulerAngles(0, 0, -30);
    boss.addChild(leftShoulder);

    const rightShoulder = new pc.Entity('RightShoulder');
    rightShoulder.addComponent('render', { type: 'cone', material: armorColor });
    rightShoulder.setLocalPosition(0.55, 0.15, 0);
    rightShoulder.setLocalScale(0.2, 0.35 + level * 0.05, 0.2);
    rightShoulder.setLocalEulerAngles(0, 0, 30);
    boss.addChild(rightShoulder);

    // Glowing core
    const coreGlow = createGlowMaterial(config.bossGlowColor, 1.0);
    const core = new pc.Entity('Core');
    core.addComponent('render', { type: 'sphere', material: coreGlow });
    core.setLocalPosition(0, -0.15, 0.5);
    core.setLocalScale(0.15, 0.15, 0.1);
    boss.addChild(core);

    // Custom properties with level scaling
    boss.velocity = new pc.Vec3(0, 0, 0);
    boss.health = config.bossHealth;
    boss.maxHealth = config.bossHealth;
    boss.attackCooldown = 0;
    boss.isAttacking = false;
    boss.baseY = 2 * sizeMultiplier;
    boss.baseScale = new pc.Vec3(2.5 * sizeMultiplier, 3 * sizeMultiplier, 2.5 * sizeMultiplier);
    boss.moveSpeed = config.bossSpeed;
    boss.level = level;
    boss.config = config;
    boss.rageAttacks = 0;

    console.log(`Boss Level ${level} "${baseConfig.name}" created`);
    return boss;
}

// Flash the boss a specific color temporarily
export function flashBoss(boss, color, level) {
    const config = LEVEL_CONFIG[level];
    boss.render.material = createGlowMaterial(color, 1.0);

    setTimeout(() => {
        if (boss && !boss.destroyed) {
            boss.render.material = createMetalMaterial(config.bossColor);
        }
    }, 200);
}

// Reset boss to starting state
export function resetBoss(boss) {
    const sizeMultiplier = 1 + (boss.level - 1) * 0.15;
    boss.setPosition(0, 2 * sizeMultiplier, -5);
    boss.health = boss.maxHealth;
    boss.attackCooldown = 1;
    boss.isAttacking = false;
    boss.setLocalScale(boss.baseScale.x, boss.baseScale.y, boss.baseScale.z);
    boss.setEulerAngles(0, 0, 0);
    boss.rageAttacks = 0;
}
